import _get from 'lodash/get';
import { formatUqlDateText, isPresent, createModel } from '@/lib/utils';
import { constants } from '@/lib/constants';
import store from '@/store';

const rowRequiresInput = (operator) => {
  const { ISNULL, ISNOTNULL, ANYEXISTS, NOTANYEXISTS, HAS, NOTHAS } = constants;
  const ops = [ISNULL, ISNOTNULL, ANYEXISTS, NOTANYEXISTS, HAS, NOTHAS];

  return ops.includes(operator);
};

const inputPresent = (item) => {
  const { usertext, userdate, operator } = item;

  if (usertext || userdate) return true;
  return rowRequiresInput(operator);
};

const isBlankRow = (item) => {
  const { usertext, userdate, operator, field } = item;

  return !usertext && !userdate && !operator && !field;
};

const isFullField = (item) => {
  const { field, _uiRows } = item;
  const splitField = field ? field.split('.') : [];

  return _uiRows.length === splitField.length;
};

const isFullRow = (item) => {
  const { operator } = item;

  return operator && isFullField(item) && inputPresent(item);
};

const validateQuery = (items) => {
  return items.every(item => isFullRow(item) || isBlankRow(item));
};

const uiRowObject = () => {
  return {
    field: '',
    usertext: '',
    userdate: '',
    operator: '',
    isAssociation: false,
    className: null,
  };
};

const createFilterRow = (keys = {}, queryKey = '', clearFilterId = false) => {
  keys = {
    ...keys,
    _uiRows: [uiRowObject()],
  };
  const model = createModel('FilterRow', keys);
  store.dispatch('advancedSearch/addUqlItem', { item: model, queryKey, clearFilterId });
};

/**
 * Increnments or decrements number, determined by paren type
 * @param {Object} item - object of user input from uqlItem
 * @param {number} level - number increnmented if opening paren, decremented if closing paren
 * @return {number} number of level on filterRow that will determine placement of parens in uql sring
 */
const setLevels = (item, level) => {
  const { type = '', text = '' } = _get(item, 'value', {});

  if (type === 'parens') {
    if (text === '(') level += 1;
    if (text === ')') level -= 1;
  }

  return level;
};

/**
 * Rows with same level get assigned the same conjunction
 * @param {Object[]} items - array of user input objects
 * @param {number} index - index of item in array passed down from setFilterRowProps()
 */
const setConjunctionByLevel = (items, index) => {
  const sliced = items.slice(index);
  const nextConj = sliced.find(el => el.type === 'conjunction');
  const item = items[index];

  if (nextConj) {
    item.row.conjunction = nextConj.value.text;
  } else {
    // all clauses on same level should have same conjunction
    const itemLevel = items.find(el => el.row && el.row.level === item.row.level);
    /* istanbul ignore else */
    if (itemLevel) item.row.conjunction = itemLevel.row.conjunction;
  }

  return item;
};

/**
 * Sets startsgroup and endsgroup properties
 * props' settings are determined by if the item directly
 * before or directly after is a type of parens
 * @param {Object[]} items - array of user input objects
 * @param {number} index - index of item in array passed down from setFilterRowProps()
 * @return {Object} modified item with startsgroup and endsgroup properites set
 */
const setGroups = (items, index) => {
  const item = items[index];
  const key = parseInt(index, 10) + 1;
  const hasValue = items && items[key] && items[key].value;
  const isItemBeforeCurrentParens = index > 0 && items[index - 1].value && items[index - 1].value.type === 'parens';
  const isItemAfterCurrentParens = index < items.length && hasValue && items[key].type === 'parens' && items[key].value.text === ')';

  // checks if item before current item is a parens object
  if (isItemBeforeCurrentParens) {
    item.row.startsgroup = 'Y';
    item.row.endsgroup = 'N';
  }

  // checks if item after current item is a parens object
  if (isItemAfterCurrentParens) {
    item.row.startsgroup = 'N';
    item.row.endsgroup = 'Y';
  }

  return item;
};

/**
 * if first item is a clause the the filterRow conjunction property
 * is set to the first conjunction after the current clause object
 * @param {Object[]} items - array of user input objects
 * @param {number} index - index of item in array passed down from setFilterRowProps()
 * @return {Object} item with filterRow conjunction property set to the first conjunction object after current clause item
 */
const setFirstClauseConjunction = (items, index) => {
  const item = items[index];

  // sets first clause's conjunction to the first conjunction after it
  if (index === '0' && item.type === 'clause') {
    const firstConjunction = items.find(el => el.type === 'conjunction');
    item.row.conjunction = firstConjunction.value.text;
  }

  return item;
};

/**
 * Takes array of objects of user input and sets the filterRow props
 * in each clause item with the correct levels and conjunctions
 * @param {Object[]} array - of objects with advancedSearch input
 * @return {Obejct[]} filterRow objects with props set to data from advancedSearch input
 */
const setFilterRowProps = (array) => {
  const items = [...array];

  for (const [index, item] of items.entries()) {
    item.sequence = index;
  }

  return items;
};

/**
 * Creates string from filterRow properties
 * @param {Object} row - FilterRow
 * @return {string} uql string with delimiter
 */
const queryString = (row) => {
  let text = '';
  if (isBlankRow(row)) return text;
  if (isPresent(row.usertext)) text = row.usertext;
  if (row.userdate) text = formatUqlDateText(row.userdate);

  return `${row.field}-${row.operator}${text}${constants.DELIM}`;
};

const buildAssociationString = (items) => {
  let q = '';

  for (const [index, item] of items.entries()) {
    if (index === items.length - 1) {
      q += item.field;
      continue;
    }

    q += `${item.field}.`;
  }

  return q;
};

const setFields = (rows) => {
  for (const row of rows) {
    if (row._uiRows.length > 1) {
      row.field = buildAssociationString(row._uiRows);
      continue;
    }

    row.field = row._uiRows[0].field;
  }

  return rows;
};

/**
 * Removes parens and conjunction objects
 * Set rows property of SavedFilter with filterRow from each uqlItem
 * @param {Object[]} uqlItems - array of userinput objects (clauses, parens, conjunctions)
 * @param {Object} state - advancedSearch state
 * @return {Object} SavedFilter Object with set filterid and FilterRows
 */
const constructSavedFilterObject = (filterRows, filter, filterId) => {
  filterRows = setFields(filterRows);

  filter.rows = filterRows.map((row) => {
    row.filterid = filterId || filter.filterid;
    return row;
  });

  return filter;
};

/**
 * Constructs string based form the filterRow properties
 * @param {Object[]} rows - array of filterRows
 * @return {string} uql query string
 */
const constructUQLString = (rows) => {
  let uqlString = '';
  let rowOne = true;
  let opens = 0;
  for (const row of rows) {
    if (rowOne) {
      if (row.startsgroup === 'Y') {
        if (row.conjunction === 'or') {
          uqlString += 'and(';
          opens += 1;
        } else if (row.conjunction === 'and') {
          uqlString += 'or(';
          opens += 1;
        }
      }

      if (row.conjunction) {
        uqlString += `${row.conjunction}(`;
        opens += 1;
      }
      rowOne = false;
    } else if (row.startsgroup === 'Y') {
      uqlString += `${row.conjunction}(`;
      opens += 1;
    }
    uqlString += queryString(row);
    if (row.endsgroup === 'Y') {
      uqlString += `)${constants.DELIM}`;
      opens -= 1;
    }
  }
  while (opens > 0) {
    uqlString += ')';
    opens -= 1;
  }
  return uqlString;
};

/**
 * Creates conjunction object for chip
 * @param {Object} item - filterRow object
 * @return {Object} conjunction object
 */
const formatConjunction = (item) => {
  const valueObj = {
    text: item.conjunction,
    type: 'conjunction',
  };
  return {
    value: valueObj,
    isConjunction: true,
    userInput: [valueObj],
  };
};

/**
 * Creates cojunction object for chip
 * @param {string} paren
 * @return {Object} paren object
 */
const formatParen = (paren) => {
  const valueObj = {
    text: paren,
    type: 'parens',
  };
  return {
    value: valueObj,
    userInput: [valueObj],
  };
};

/**
 * Recursive function that gets associations based on filterRow field.
 * Creates association objects for uqlItems if filterRow field property
 * has field with class associations.
 * @param {Object[]} associations - Mapped relations of classes in schema formatted to work with advancedSearch combobox
 * @param {string} associations[].name - Association name
 * @param {Object[]} dbFields - db properties of classname
 * @param {string[]} splitField - filterRow field property split on '.' (ex. `servicezone.zip.zip`)
 * @param {Object[]} rootGetters - vuex root getters
 * @param {number} count - interation count
 * @param {Object} [acc] - accumilates associations during recursive iterations
 * @return {Object} acc - Object that contains array of formatted associations and dbField object
 */
const getAssociations = ({ associations, dbFields, splitField, rootGetters, count, acc }) => {
  const association = associations.filter(el => splitField[count] === el.name);
  if (association.length === 0) return;

  const [dbFieldAssociation] = association;
  acc.associations.push(dbFieldAssociation);
  const newAssociations = rootGetters['advancedSearch/associations'](dbFieldAssociation.value);
  count += 1;

  const nextAssociations = getAssociations({
    associations: newAssociations,
    dbFields,
    splitField,
    rootGetters,
    count,
    acc,
  });

  if (nextAssociations) return nextAssociations;

  const newDbFields = rootGetters['advancedSearch/db_properties'](dbFieldAssociation.value);
  const [field] = newDbFields.filter(el => splitField[splitField.length - 1] === el.name);
  acc.field = field;

  return acc;
};

/**
 * Formats array of associations of a designated class into
 * a format for use in the Vuetify combobox
 * @param {string} className
 * @param {Object} rootGetters - All getters from entire store
 * @return {Object[]} Array of formatted data of associations of className
 */
const mapperRelationships = (className, rootGetters, additionalFields) => {
  const mapper_relationships = rootGetters['meta/mapper_relationships'](className, { advSearch: true, sortAlphabetically: true, additionalFields });
  const associations = mapper_relationships.map((el) => {
    const metaLabel = _get(el, 'data.labels.label', '');
    const needsLabel = (metaLabel.charAt(0) !== metaLabel.charAt(0).toUpperCase());
    let label = '';
    if (needsLabel) {
      const type = _get(el, 'data.type');
      if (type === 'object') label = rootGetters['meta/class_label'](el.data.classname);
      if (type === 'array') label = rootGetters['meta/class_label'](el.data.classname, true);
    } else if (el._isCustom) {
      label = el.label;
    } else label = metaLabel;

    return {
      ...el,
      _ui_label: `[${label}]`,
      field: el.name,
    };
  });

  return associations;
};

/**
 * Sets data for associations on _uiRows
 * @param {Object} payload
 * @param {Object} payload.uiRow - Object to be set
 * @param {String} payload.field - String used to find assoction meta
 * @param {Object[]} payload.associations - Mapper relationships of first field
 * @param {Object[]} payload.newRows - Array of _uiRows
 * @param {Function} payload.getClassAssociations - advancedSearch/associations getter
 * @return {Object} uiRow - Updated uiRow object
 */
const associationUiRows = (payload) => {
  let { uiRow, field, index, associations, newRows, getClassAssociations, row, isLast } = payload;
  let assocData = null;
  uiRow.isAssociation = true;

  if (index === 0) {
    assocData = associations.find(data => data.name === field);
  } else {
    const previousClassName = newRows[index - 1].className;
    associations = getClassAssociations(previousClassName);
    assocData = associations.find(data => data.name === field);
  }

  if (isLast) {
    uiRow.operator = row.operator;
    const { usertext, userdate } = row;
    if (usertext) uiRow.usertext = usertext;
    if (userdate) uiRow.userdate = userdate;
  }

  if (assocData) uiRow.className = assocData.data.classname;
  uiRow.field = field;

  return uiRow;
};

const buildUiRows = (row, rootGetters, className) => {
  const fieldArray = row.field ? row.field.split('.') : [];
  const getClassAssociations = (classname) => rootGetters['advancedSearch/associations'](classname);
  const newRows = [];
  const classNameAssociations = getClassAssociations(className);

  // handling of empty rows
  if (!fieldArray.length) {
    const uiRow = uiRowObject();
    for (const key of Object.keys(uiRow)) {
      uiRow[key] = row[key];
    }
    newRows.push(uiRow);
  }

  for (const [index, field] of fieldArray.entries()) {
    const uiRow = uiRowObject();
    const isAssociation = classNameAssociations.find(prop => prop.name === field);
    // queries with relations
    if (fieldArray.length > 1 || isAssociation) {
      newRows.push(associationUiRows({
        uiRow,
        field,
        index,
        associations: classNameAssociations,
        newRows,
        getClassAssociations,
        row,
        // Need to prevent operator from being added to each uiRow object.
        // Only needs to be on the last when it's an association of an association.
        isLast: index === fieldArray.length - 1,
      }));
    } else {
      // simple queries
      for (const key of Object.keys(uiRow)) {
        if (row[key] && key !== 'field') uiRow[key] = row[key];
        if (key === 'field') uiRow.field = field;
      }
      newRows.push(uiRow);
    }
  }

  return newRows;
};

const formatFilterUiRows = (filter, rootGetters, className) => {
  const { rows } = filter;

  for (const row of rows) {
    row._uiRows = buildUiRows(row, rootGetters, className);
  }

  return rows;
};

/**
 * Gets either full association combobxo object or key to create / map object.
 */
const customFieldData = (className, key) => {
  const getLabel = (label) => store.getters['translations/label'](`{${label}}l`);
  const data = {
    Discount: {
      associations: [
        {
          _isCustom: true,
          name: '_siteids_',
          className: 'Discount',
          label: getLabel('Discount._siteids_'),
          data: {
            classname: 'DiscountSite',
            type: 'array',
            queryable: true,
            property_meta: {
              source: 'mapper_relationship',
            },
          },
        },
      ],
    },
  };

  return _get(data, `${className}.${key}`);
};

export {
  setFilterRowProps,
  constructSavedFilterObject,
  constructUQLString,
  mapperRelationships,
  setLevels,
  setConjunctionByLevel,
  setGroups,
  setFirstClauseConjunction,
  queryString,
  formatConjunction,
  formatParen,
  getAssociations,
  uiRowObject,
  createFilterRow,
  formatFilterUiRows,
  validateQuery,
  setFields,
  customFieldData,
};
